import { FormulaMatch, FormulaProfile, IntakeAnswers } from '../types/conversation';

const formulaCatalog: FormulaProfile[] = [
  {
    id: 'jin-gui-shen-qi-wan',
    name: 'Jin Gui Shen Qi Wan',
    nickname: 'Kidney yang support classic',
    pattern: 'Kidney yang deficiency with cold and fatigue',
    focus: ['libido', 'energy', 'circulation'],
    description:
      'Warms and gently tonifies kidney yang to rekindle drive, circulation, and lower-back warmth. Often paired with lifestyle support that emphasises nourishing foods and restorative rest.',
    ingredients: [
      'Shu Di Huang',
      'Shan Zhu Yu',
      'Shan Yao',
      'Fu Ling',
      'Mu Dan Pi',
      'Ze Xie',
      'Fu Zi',
      'Gui Zhi'
    ],
    form: 'teapills',
    caution: 'Avoid during acute feverish illness or when working with high-dose stimulants. Flag pregnancy for clinician review.',
    spotlight: 'Pairs well with moxibustion or light movement to warm yang.',
    lifestyleTips: [
      'Favour warming spices such as cinnamon and ginger.',
      'Keep lower back and feet covered, especially in cooler environments.',
      'Balance stamina work with adequate sleep to avoid yang depletion.'
    ]
  },
  {
    id: 'liu-wei-di-huang-wan',
    name: 'Liu Wei Di Huang Wan',
    nickname: 'Kidney yin replenisher',
    pattern: 'Kidney yin deficiency with heat signs and dryness',
    focus: ['libido', 'sleep', 'hydration'],
    description:
      'Nourishes kidney yin and essence for users who run warm, feel parched, or notice restlessness in the evenings. Helps balance fluids to smooth hormonal ebbs.',
    ingredients: [
      'Shu Di Huang',
      'Shan Zhu Yu',
      'Shan Yao',
      'Fu Ling',
      'Mu Dan Pi',
      'Ze Xie'
    ],
    form: 'granules',
    caution: 'Check with clinician if digestion is sluggish or if you currently take diuretics.',
    spotlight: 'Layer with yin-nourishing foods (pear, goji, black sesame).',
    lifestyleTips: [
      'Schedule gentle wind-down rituals before bed.',
      'Hydrate with room-temperature or slightly warm fluids during the day.',
      'Add restorative yin yoga or stretching to evening routine.'
    ]
  },
  {
    id: 'xiao-yao-san',
    name: 'Xiao Yao San',
    nickname: 'Free-and-easy wanderer blend',
    pattern: 'Liver qi stagnation with stress and digestive tension',
    focus: ['stress', 'mood', 'cycle'],
    description:
      'Smooths liver qi to ease emotional tightness, PMS mood swings, and upper digestive bloating. Offers a grounded lift without over-stimulating.',
    ingredients: [
      'Chai Hu',
      'Dang Gui',
      'Bai Shao',
      'Bai Zhu',
      'Fu Ling',
      'Gan Cao',
      'Bo He',
      'Sheng Jiang'
    ],
    form: 'granules',
    caution: 'Pause during acute colds with fever and confirm herb-drug interactions if using SSRIs or hormonal treatments.',
    spotlight: 'Especially supportive when journaling or therapy is part of the plan.',
    lifestyleTips: [
      'Take mindful breaks through the workday to release jaw and shoulders.',
      'Favour warm, cooked meals to avoid taxing digestion.',
      'Track menstrual cycle mood shifts to see improvements over time.'
    ]
  },
  {
    id: 'gui-pi-tang',
    name: 'Gui Pi Tang',
    nickname: 'Spleen and heart harmony tonic',
    pattern: 'Spleen qi and heart blood deficiency',
    focus: ['energy', 'sleep', 'focus'],
    description:
      'Builds spleen qi and nourishes heart blood for those feeling worn-down, unfocused, or waking unrefreshed. Supports gentle digestion while calming the mind.',
    ingredients: [
      'Ren Shen',
      'Bai Zhu',
      'Fu Ling',
      'Gan Cao',
      'Huang Qi',
      'Suan Zao Ren',
      'Long Yan Rou',
      'Dang Gui',
      'Mu Xiang'
    ],
    form: 'capsules',
    caution: 'Monitor if you experience very loose stools or are sensitive to warming tonics.',
    spotlight: 'Ideal companion to consistent meal timing and blood-sugar balance.',
    lifestyleTips: [
      'Eat breakfast within 60 minutes of waking to anchor spleen qi.',
      'Mix in gentle strength training to build vitality steadily.',
      'Cut caffeine by mid-day to protect evening rest.'
    ]
  },
  {
    id: 'an-shen-bu-xin-wan',
    name: 'An Shen Bu Xin Wan',
    nickname: 'Calm the spirit elixir',
    pattern: 'Heart-kidney miscommunication with anxiety and restless sleep',
    focus: ['sleep', 'stress', 'mood'],
    description:
      'Settles the spirit for users lying awake with spiralling thoughts or vivid dreams. Offers gentle calm without heavy sedation.',
    ingredients: [
      'Ren Shen',
      'Fu Ling',
      'Yuan Zhi',
      'Dang Gui',
      'Suan Zao Ren',
      'Wu Wei Zi'
    ],
    form: 'teapills',
    caution: 'Consult a clinician if taking strong sedatives or if insomnia comes with severe depression.',
    spotlight: 'Pairs beautifully with breathwork and screen-free evenings.',
    lifestyleTips: [
      'Power down devices 60 minutes before bed and try soft lighting.',
      'Practise 4-7-8 breathing or guided meditations nightly.',
      'Keep a dream journal to track progress and insights.'
    ]
  }
];

type Evaluator = (answers: IntakeAnswers) => FormulaMatch | null;

const getArray = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const matchers: Record<string, Evaluator> = {
  'jin-gui-shen-qi-wan': (answers) => {
    const focus = getArray(answers.focusAreas);
    const temp = answers.temperature as string | undefined;
    const energy = answers.energyLevel as string | undefined;
    const reasons: string[] = [];
    let score = 0;

    if (focus.includes('libido')) {
      score += 3;
      reasons.push('You highlighted libido and vitality support.');
    }
    if (focus.includes('energy')) {
      score += 2;
      reasons.push('Energy replenishment is a priority for you.');
    }
    if (temp === 'runsCold') {
      score += 2;
      reasons.push('You noted feeling cold or preferring warmth.');
    }
    if (energy === 'very-low' || energy === 'low') {
      score += 1;
      reasons.push('We heard that stamina has been dipping.');
    }
    if (!score) {
      return null;
    }
    return { formula: formulaCatalog[0], score, reasons };
  },
  'liu-wei-di-huang-wan': (answers) => {
    const focus = getArray(answers.focusAreas);
    const temp = answers.temperature as string | undefined;
    const sleep = answers.sleepQuality as string | undefined;
    const tongue = answers.tongue as string | undefined;
    const reasons: string[] = [];
    let score = 0;

    if (focus.includes('libido') || focus.includes('sleep')) {
      score += 2;
      reasons.push('You asked for sustainable yin and rest support.');
    }
    if (temp === 'runsWarm') {
      score += 2;
      reasons.push('You mentioned running warm or craving cool environments.');
    }
    if (sleep === 'restless' || sleep === 'middle-of-night') {
      score += 1;
      reasons.push('Evenings feel restless or sleep breaks in the night.');
    }
    if (tongue === 'red-thin' || tongue === 'scarlet') {
      score += 1;
      reasons.push('Tongue picture suggests yin is a bit depleted.');
    }
    if (!score) {
      return null;
    }
    return { formula: formulaCatalog[1], score, reasons };
  },
  'xiao-yao-san': (answers) => {
    const focus = getArray(answers.focusAreas);
    const stress = answers.stressLevel as string | undefined;
    const digestion = answers.digestive as string | undefined;
    const cycle = answers.cycle as string | undefined;
    const reasons: string[] = [];
    let score = 0;

    if (focus.includes('stress') || focus.includes('mood')) {
      score += 3;
      reasons.push('You emphasised smoothing mood and stress.');
    }
    if (stress === 'high') {
      score += 2;
      reasons.push('Stress feels high and tense lately.');
    }
    if (digestion === 'bloating' || digestion === 'alternating') {
      score += 1;
      reasons.push('Upper digestion feels stuck or alternating.');
    }
    if (cycle === 'pms-irritability' || cycle === 'irregular') {
      score += 1;
      reasons.push('Cycle symptoms show liver qi involvement.');
    }
    if (!score) {
      return null;
    }
    return { formula: formulaCatalog[2], score, reasons };
  },
  'gui-pi-tang': (answers) => {
    const focus = getArray(answers.focusAreas);
    const energy = answers.energyLevel as string | undefined;
    const sleep = answers.sleepQuality as string | undefined;
    const tongue = answers.tongue as string | undefined;
    const digestion = answers.digestive as string | undefined;
    const reasons: string[] = [];
    let score = 0;

    if (focus.includes('energy') || focus.includes('focus')) {
      score += 3;
      reasons.push('You are looking for sustainable daytime energy.');
    }
    if (sleep === 'unrefreshed') {
      score += 1;
      reasons.push('Waking up unrefreshed points to heart-spleen support.');
    }
    if (energy === 'low') {
      score += 1;
      reasons.push('Energy dips through the day.');
    }
    if (tongue === 'pale') {
      score += 1;
      reasons.push('Tongue picture hints at qi and blood needing nourishment.');
    }
    if (digestion === 'loose') {
      score -= 1;
      reasons.push('Formula is warming--loose stools mean we balance dosage.');
    }
    if (score <= 0) {
      return null;
    }
    return { formula: formulaCatalog[3], score, reasons };
  },
  'an-shen-bu-xin-wan': (answers) => {
    const focus = getArray(answers.focusAreas);
    const sleep = answers.sleepQuality as string | undefined;
    const stress = answers.stressLevel as string | undefined;
    const reasons: string[] = [];
    let score = 0;

    if (focus.includes('sleep')) {
      score += 3;
      reasons.push('Restful sleep is top priority for you.');
    }
    if (sleep === 'restless' || sleep === 'middle-of-night') {
      score += 1;
      reasons.push('You wake easily or with active dreaming.');
    }
    if (stress === 'moderate' || stress === 'high') {
      score += 1;
      reasons.push('Calming the spirit can help manage daily stress load.');
    }
    if (!score) {
      return null;
    }
    return { formula: formulaCatalog[4], score, reasons };
  }
};

const safetyFlags = ['pregnant', 'fertilityTreatments', 'bloodPressure', 'autoimmune'] as const;

type SafetyFlag = (typeof safetyFlags)[number];

export const deriveSafetyNotes = (answers: IntakeAnswers): string[] => {
  const flagged = getArray(answers.safetyFlags) as SafetyFlag[];
  const notes: string[] = [];

  if (flagged.includes('pregnant')) {
    notes.push('Pregnancy noted: every formula will be double-checked by a licensed TCM practitioner before fulfilment.');
  }
  if (flagged.includes('bloodPressure')) {
    notes.push('Blood pressure considerations mean we moderate warming or stimulating herbs.');
  }
  if (flagged.includes('autoimmune')) {
    notes.push('Autoimmune history flagged. We review each herb for immune-modulating effects.');
  }
  if (flagged.includes('fertilityTreatments')) {
    notes.push('Fertility support in progress: we coordinate dosing with your clinical team.');
  }

  if (!notes.length) {
    notes.push('No immediate safety flags detected. We still review every plan before shipping.');
  }

  return notes;
};

export const evaluateFormulas = (answers: IntakeAnswers): FormulaMatch[] => {
  const matches = Object.values(matchers)
    .map((evaluate) => evaluate(answers))
    .filter((entry): entry is FormulaMatch => Boolean(entry))
    .sort((a, b) => b.score - a.score);

  if (!matches.length) {
    const fallback: FormulaMatch = {
      formula: {
        id: 'classic-balance',
        name: 'Custom Balance Draft',
        nickname: 'Practitioner-curated trio',
        pattern: 'Requires mixed-pattern review',
        focus: ['general'],
        description:
          'Your responses map across multiple patterns. Our team blends a balanced base and schedules a practitioner-led check-in to fine tune dosage.',
        ingredients: ['To be finalised after practitioner review'],
        form: 'custom',
        caution: 'Tailored once a clinician verifies your intake.',
        lifestyleTips: [
          'Expect a practitioner note within 12 hours for clarity and dosing.',
          'Capture any additional observations in the chat journal so we can personalise quickly.',
          'Stay hydrated and keep meals warm while we finalise the plan.'
        ]
      },
      score: 1,
      reasons: ['Responses spanned multiple patterns. Practitioner review queued.']
    };

    return [fallback];
  }

  return matches;
};

export const catalogue = formulaCatalog;
